import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { supabase, fromTable } from '@/integrations/supabase/client';
import { CalendarIcon, Users } from 'lucide-react';
import { Calendar } from '@/components/ui/calendar';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import EmailImporter from './EmailImporter';
import { EmailImportResult } from '@/types/collective-feedback';
import { FeedbackContext } from '@/types/feedback';

const formSchema = z.object({
  title: z.string().min(3, 'Le titre est requis (3 caractères minimum)'),
  description: z.string().optional(),
  context_type: z.enum(['recruitment', 'freelance', '360', 'all'] as const),
  event_type: z.string().optional(),
  event_date: z.date().optional(),
  event_location: z.string().optional(),
  message: z.string().optional(),
  send_immediately: z.boolean().default(false),
});

type FormValues = z.infer<typeof formSchema>;

const CollectiveFeedbackForm: React.FC = () => {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importedEmails, setImportedEmails] = useState<EmailImportResult | null>(null);
  
  // Initialize the form
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: '',
      description: '',
      context_type: 'all',
      event_type: '',
      message: '',
      send_immediately: false,
    },
  });

  const handleEmailImport = (result: EmailImportResult) => {
    setImportedEmails(result);
  };

  const onSubmit = async (data: FormValues) => {
    if (!user) {
      toast.error("Vous devez être connecté pour effectuer cette action");
      return;
    }

    if (!importedEmails || importedEmails.validEmails.length === 0) {
      toast.error("Vous devez importer au moins une adresse email valide");
      return;
    }

    setIsSubmitting(true);

    try {
      // Insert collective feedback request
      const { data: requestData, error: requestError } = await fromTable('collective_feedback_requests')
        .insert({
          title: data.title,
          description: data.description,
          context_type: data.context_type,
          event_type: data.event_type,
          event_date: data.event_date ? data.event_date.toISOString() : null,
          event_location: data.event_location,
          message: data.message,
          status: 'pending',
          creator_id: user.id,
          ...(profile && 'corporate_group_id' in profile ? { corporate_group_id: profile.corporate_group_id } : {}),
        })
        .select();

      if (requestError) throw requestError;
      
      if (!requestData || requestData.length === 0) {
        throw new Error("Erreur lors de la création de la demande");
      }

      const requestId = requestData[0].id;

      // Insert recipients
      const recipients = importedEmails.validEmails.map(({ email, name }) => ({
        request_id: requestId,
        email,
        name: name || null,
        status: 'pending',
      }));

      const { error: recipientsError } = await fromTable('collective_feedback_recipients')
        .insert(recipients);

      if (recipientsError) throw recipientsError;

      // Send emails if immediate sending is requested
      if (data.send_immediately) {
        const { error: emailError } = await supabase.functions.invoke('send-collective-feedback', {
          body: { 
            requestId
          }
        });
        
        if (emailError) {
          console.error("Erreur lors de l'envoi des emails:", emailError);
          toast.warning("Demande créée mais l'envoi des emails a échoué");
        } else {
          // Update status to 'sent' for all recipients
          await fromTable('collective_feedback_recipients')
            .update({ status: 'sent', sent_at: new Date().toISOString() })
            .eq('request_id', requestId);

          // Update request status to 'active'
          await fromTable('collective_feedback_requests')
            .update({ status: 'active' })
            .eq('id', requestId);
        }
      }

      toast.success("Demande de feedback collective créée avec succès", {
        description: data.send_immediately 
          ? `${importedEmails.validEmails.length} emails ont été envoyés`
          : `${importedEmails.validEmails.length} destinataires ajoutés`,
      });

      // Redirect to dashboard
      navigate('/dashboard');
    } catch (error: any) {
      console.error("Erreur lors de la création de la demande:", error);
      toast.error("Une erreur est survenue", {
        description: error.message || "Impossible de créer la demande",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Demande de feedback collective
        </CardTitle>
        <CardDescription>
          Envoyez une demande de feedback à plusieurs destinataires simultanément
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-6">
              <div className="grid gap-6 grid-cols-1 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Titre de la demande</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Feedback sur la conférence du 12 avril" {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="context_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Contexte</FormLabel>
                      <Select onValueChange={field.onChange} defaultValue={field.value}>
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Sélectionner un contexte" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="recruitment">Recrutement</SelectItem>
                          <SelectItem value="freelance">Mission / Projet</SelectItem>
                          <SelectItem value="360">Évaluation 360°</SelectItem>
                          <SelectItem value="all">Autre</SelectItem>
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Description du contexte de la demande de feedback" 
                        className="min-h-[80px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <div className="grid gap-6 grid-cols-1 md:grid-cols-3">
                <FormField
                  control={form.control}
                  name="event_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Type d'événement</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Conférence, Formation..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="event_date"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>Date de l'événement</FormLabel>
                      <Popover>
                        <PopoverTrigger asChild>
                          <FormControl>
                            <Button
                              variant={"outline"}
                              className={cn(
                                "w-full pl-3 text-left font-normal",
                                !field.value && "text-muted-foreground"
                              )}
                            >
                              {field.value ? (
                                format(field.value, 'PPP', { locale: fr })
                              ) : (
                                <span>Sélectionner une date</span>
                              )}
                              <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                            </Button>
                          </FormControl>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0" align="start">
                          <Calendar
                            mode="single"
                            selected={field.value}
                            onSelect={field.onChange}
                            initialFocus
                          />
                        </PopoverContent>
                      </Popover>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                
                <FormField
                  control={form.control}
                  name="event_location"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Lieu</FormLabel>
                      <FormControl>
                        <Input placeholder="Ex: Paris, En ligne..." {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              
              <FormField
                control={form.control}
                name="message"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Message aux destinataires</FormLabel>
                    <FormControl>
                      <Textarea 
                        placeholder="Message personnalisé qui sera inclus dans l'email de demande" 
                        className="min-h-[100px]"
                        {...field} 
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              
              <Separator className="my-6" />
              
              <div>
                <h3 className="text-base font-medium mb-4">Importation des destinataires</h3>
                <EmailImporter 
                  onImportComplete={handleEmailImport} 
                  isLoading={isSubmitting}
                />
              </div>
              
              <FormField
                control={form.control}
                name="send_immediately"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Envoyer immédiatement</FormLabel>
                      <FormDescription>
                        Si coché, les emails seront envoyés dès la création de la demande
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />
            </div>
          </form>
        </Form>
      </CardContent>
      <CardFooter className="flex justify-between">
        <Button variant="outline" onClick={() => navigate(-1)}>Annuler</Button>
        <Button 
          onClick={form.handleSubmit(onSubmit)} 
          disabled={isSubmitting || !importedEmails || importedEmails.validEmails.length === 0}
        >
          {isSubmitting ? 'Création en cours...' : 'Créer la demande'}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default CollectiveFeedbackForm;
